import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import {JobUpload} from "@/types";
import axios from "axios";

const infiniteRetryDelays = {
    values(): Iterator<number> {
        let attempt = 0;
        return {
            next() {
                const delay = attempt === 0 ? 0 : 1000 * Math.min(300, Math.pow(2, attempt - 1));
                attempt++;
                return {value: delay, done: false};
            },
        };
    },
} as unknown as number[];

export type UploadParams = {
    file: File,
    name: string,
    partSize: number,
    maxConcurrentParts?: number,
    signal: AbortSignal,
    progressCallback: (percentage: number, secondsLeft?: number) => void,
    mediaType?: 'video' | 'audio',
    createUploadRetries?: number,
    cloudfrontFailoverAttempts?: number,
    useCloudfront?: boolean,
}

export default async function uploadFile({
    file,
    name,
    partSize,
    maxConcurrentParts = 6,
    signal,
    progressCallback,
    mediaType,
    createUploadRetries = 3,
    cloudfrontFailoverAttempts = 3,
    useCloudfront = window.userCountry !== 'US'
}: UploadParams): Promise<string> {
    return new Promise((resolve, reject) => {
        const uppy = new Uppy({autoProceed: true});
        const partAttempts = new Map<number, number>();

        let forceDisableCloudfront = false;

        uppy.use(AwsS3, {
            shouldUseMultipart: true,
            getChunkSize: () => partSize,
            retryDelays: infiniteRetryDelays,
            limit: maxConcurrentParts,

            createMultipartUpload: async () => {
                for (let attempt = 0; ; attempt++) {
                    try {
                        const {data} = await axios.post(route('upload.multipart.create'), {
                            id: name,
                            fileName: file.name,
                            fileSize: file.size,
                            ...(mediaType && {media_type: mediaType}),
                        });
                        return {uploadId: data.s3UploadId, key: name};
                    } catch (err) {
                        if (signal.aborted || attempt >= createUploadRetries) throw err;
                        const delay = 1000 * Math.pow(2, attempt);
                        await new Promise(r => setTimeout(r, delay));
                    }
                }
            },

            signPart: async (_file, {uploadId: s3UploadId, key, partNumber}) => {
                let useCloudfrontForPart = forceDisableCloudfront ? false : useCloudfront
                if(useCloudfrontForPart) {
                    const attempts = (partAttempts.get(partNumber) || 0) + 1;
                    partAttempts.set(partNumber, attempts);
                    if (attempts === cloudfrontFailoverAttempts) {
                        forceDisableCloudfront = true;
                    }
                }

                const {data} = await axios.get(route('upload.multipart.sign-part', {
                    s3UploadId,
                    partNumber,
                    uploadId: key,
                    use_cloudfront: useCloudfrontForPart,
                }));
                return {url: data.url};
            },

            listParts: async () => [],

            completeMultipartUpload: async (_file, {uploadId: s3UploadId, key, parts}) => {
                for (let attempt = 0; ; attempt++) {
                    try {
                        await axios.post(route('upload.multipart.complete'), {
                            uploadId: key,
                            s3UploadId,
                            parts: parts.map(p => ({PartNumber: p.PartNumber, ETag: p.ETag})),
                        });
                        return {};
                    } catch (err) {
                        if (signal.aborted || attempt >= createUploadRetries) throw err;
                        const delay = 1000 * Math.pow(2, attempt);
                        await new Promise(r => setTimeout(r, delay));
                    }
                }
            },

            abortMultipartUpload: async () => {},
        });

        const uploadStart = Date.now();

        uppy.addFile({name: file.name, type: file.type || 'application/octet-stream', data: file});

        uppy.on('upload-progress', (_file, progress) => {
            if (signal.aborted || !progress.bytesTotal) return;
            const elapsed = (Date.now() - uploadStart) / 1000;
            const percentage = progress.bytesUploaded / progress.bytesTotal;
            const speed = elapsed > 0 ? progress.bytesUploaded / elapsed : 0;
            const secondsLeft = speed > 0 ? (progress.bytesTotal - progress.bytesUploaded) / speed : undefined;
            progressCallback(percentage, secondsLeft);
        });

        signal.addEventListener('abort', () => {
            uppy.cancelAll();
            uppy.destroy();
            reject(new Error('User aborted the upload'));
        });

        uppy.on('complete', (result) => {
            uppy.destroy();
            if (signal.aborted) return;
            if (result.failed?.length) {
                reject(new Error(result.failed[0].error ?? 'Upload failed'));
            } else {
                progressCallback(1, 0);
                resolve(name);
            }
        });

        uppy.on('error', (error: any) => {
            uppy.destroy();
            if (!signal.aborted) {
                const message = error.details
                    ? `${error.message}: ${error.details}`
                    : error.message || 'Upload failed';
                reject(new Error(message));
            }
        });
    });
}


export async function finalizeUpload(uploadId: string, jobId: number): Promise<JobUpload> {
    try {
        const res = await axios.post(route('upload.finalize'), {id: uploadId, jobId})
        return res.data as JobUpload;
    } catch (e: any) {
        const errorMessage = e.response?.data?.message || e.message
        throw new Error(errorMessage)
    }
}
